import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, Truck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { RETURN_URL_PARAM, sanitizeStaffReturnPath, buildCustomerLoginUrl, buildStaffLoginUrl } from '../utils/authReturnUrl';
import { isSubdomainRoutingEnabled, toWorkspaceUrl, wsPath } from '../utils/siteHosts';

/**
 * Streamlined sign-in for shipping agents:
 *   Step 1 — enter email           → POST /auth/agent/request-code
 *   Step 2 — enter the 6-digit code → POST /auth/mfa/verify (existing)
 *
 * No password, no extra fields. The page is a direct landing target for the
 * shipping label QR code (fallback) and the post-login redirect.
 */
function AgentAuthPage() {
  const {
    auth,
    isAuthenticated,
    requestAgentLoginCode,
    completeMfa,
    resendMfaCode,
    cancelMfaFlow,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState(auth.email || '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const phase: 'EMAIL' | 'CODE' = auth.mfaRequired && auth.challengeId ? 'CODE' : 'EMAIL';

  const targetAfterLogin = useMemo(() => {
    const raw = searchParams.get(RETURN_URL_PARAM);
    const sanitized = raw ? sanitizeStaffReturnPath(raw) : null;
    if (sanitized) return sanitized;
    return wsPath('/workspace/delivery/scan');
  }, [searchParams]);

  // After tokens are issued, send the agent straight to the scan page (or the
  // returnUrl if it's a workspace path — typically /workspace/delivery/scan?code=…).
  useEffect(() => {
    if (!isAuthenticated) return;
    if (auth.type === 'CUSTOMER') {
      window.location.assign(buildCustomerLoginUrl('/account'));
      return;
    }
    const destination = targetAfterLogin;
    if (isSubdomainRoutingEnabled()) {
      window.location.assign(toWorkspaceUrl(destination));
      return;
    }
    navigate(destination, { replace: true });
  }, [auth.type, isAuthenticated, navigate, targetAfterLogin]);

  // Tick the resend cooldown.
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = window.setTimeout(() => setResendCountdown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCountdown]);

  if (isAuthenticated && auth.type && auth.type !== 'CUSTOMER') {
    return <Navigate to={targetAfterLogin} replace />;
  }

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await requestAgentLoginCode(email);
      setInfo('We just emailed you a 6-digit code. Check your inbox.');
      setResendCountdown(60);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'We could not send your sign-in code. Please contact your admin.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const trimmed = code.replace(/[^0-9]/g, '').trim();
      await completeMfa(trimmed);
      // Redirect happens via the effect above once auth state updates.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setInfo(null);
    try {
      await resendMfaCode();
      setInfo('A new code is on its way to your inbox.');
      setResendCountdown(60);
    } catch (err) {
      const retryAfter = (err as { retryAfterSeconds?: number })?.retryAfterSeconds;
      if (typeof retryAfter === 'number' && retryAfter > 0) {
        setResendCountdown(retryAfter);
      }
      setError(err instanceof Error ? err.message : 'Unable to resend code.');
    }
  };

  const handleStartOver = () => {
    cancelMfaFlow();
    setCode('');
    setInfo(null);
    setError(null);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-slate-50 via-white to-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-600/10 text-brand-700">
            <Truck className="h-5 w-5" strokeWidth={2.25} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Shipping agent sign-in
            </h1>
            <p className="text-xs text-slate-500">
              Email + 6-digit code. No password.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
          {phase === 'EMAIL' ? (
            <form className="space-y-4" onSubmit={handleRequestCode}>
              <div>
                <label
                  htmlFor="agent-email"
                  className="mb-1 block text-xs font-medium text-slate-700"
                >
                  Your work email
                </label>
                <input
                  id="agent-email"
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="agent@example.com"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </div>

              {error && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </p>
              )}
              {info && !error && (
                <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  {info}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || !email.trim()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Mail className="h-4 w-4" strokeWidth={2.25} />
                {submitting ? 'Sending code…' : 'Email me a sign-in code'}
              </button>

              <p className="text-center text-[11px] leading-snug text-slate-500">
                Are you staff or admin?{' '}
                <a href={buildStaffLoginUrl('/')} className="font-medium text-brand-600 hover:underline">
                  Staff sign-in
                </a>
              </p>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={handleVerify}>
              <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Sent a 6-digit code to{' '}
                <span className="font-medium text-slate-900">{auth.email}</span>.
              </div>

              <div>
                <label
                  htmlFor="agent-code"
                  className="mb-1 block text-xs font-medium text-slate-700"
                >
                  Enter the 6-digit code
                </label>
                <input
                  id="agent-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-center text-lg font-semibold tracking-[0.4em] text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
              </div>

              {error && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </p>
              )}
              {info && !error && (
                <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  {info}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || code.length < 4}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? 'Verifying…' : 'Sign in'}
              </button>

              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={handleStartOver}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.25} />
                  Use a different email
                </button>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCountdown > 0}
                  className="font-medium text-brand-600 hover:underline disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {resendCountdown > 0
                    ? `Resend in ${resendCountdown}s`
                    : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400">
          Need help? Contact your admin.
        </p>
      </div>
    </div>
  );
}

export default AgentAuthPage;
